import { useEffect, useState } from "react"

import { DialogTitle } from "@/components/ui/dialog"

const MESSAGES = [
  "Analyzing your business…",
  "Confirming your details…",
  "Fine-tuning your profile…",
  "Making it perfect for you…",
  "Almost there…",
]

/**
 * How long the ring takes to fill, and the floor on how long this step lasts.
 *
 * Overridable so E2E can skip the pause instead of burning five seconds a
 * run; the default is the one borrowers get.
 */
export const LOADING_MS = loadingMs(import.meta.env.VITE_ONBOARDING_LOADING_MS)

/**
 * Reads the override, falling back to the five seconds borrowers get.
 *
 * Checked for finiteness rather than truthiness, so `0` is honoured: skipping
 * the pause outright is the point of the override.
 */
function loadingMs(value: string | undefined): number {
  const parsed = Number(value)
  if (value === undefined || value === "" || !Number.isFinite(parsed)) {
    return 5000
  }
  return Math.max(0, parsed)
}

const RADIUS = 44
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

interface Props {
  /** Called once the wait is over. */
  onDone: () => void
}

/**
 * The pause between the last question and the reveal.
 *
 * The answers are already saved by the steps that asked them; this exists so
 * the number at the end reads as the result of some work rather than as an
 * instant page swap.
 */
export function WorkingStep({ onDone }: Props) {
  const [message, setMessage] = useState(0)

  useEffect(() => {
    const timer = setInterval(
      () => setMessage((i) => (i + 1) % MESSAGES.length),
      1000,
    )
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const timer = setTimeout(onDone, LOADING_MS)
    return () => clearTimeout(timer)
  }, [onDone])

  return (
    <div
      className="flex min-h-[340px] flex-col items-center justify-center px-9 pt-6 pb-[34px] text-center"
      data-testid="onboarding-loading"
    >
      <DialogTitle className="sr-only">Working on it</DialogTitle>

      <svg viewBox="0 0 100 100" className="mb-6 size-[100px]">
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth="5"
          className="stroke-slate-200"
        />
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE}
          style={{ animationDuration: `${LOADING_MS}ms` }}
          className="stroke-primary animate-[onboarding-ring_linear_forwards] motion-reduce:animate-none"
        />
      </svg>

      {/* Not a live region: the text turns over every second, and the
          sr-only title above already says what is happening. Announcing each
          turn would interrupt five times over one pause. */}
      <p className="min-h-[22px] text-base font-medium" aria-hidden>
        {MESSAGES[message]}
      </p>
    </div>
  )
}
